import { useState, useEffect, useCallback } from "react";
import { SERVER_URL, setGate, getDevice, setDevice, clearDevice } from "./socket";

// Pre-launch gate: black screen, two passwords (step 1 → step 2), a captcha on each step that refreshes
// on demand and automatically after every wrong try.
export default function Gate({ onPass }) {
  const [step, setStep] = useState(1);
  const [stepToken, setStepToken] = useState("");
  const [pw, setPw] = useState("");
  const [cap, setCap] = useState({ id: "", svg: "" });
  const [answer, setAnswer] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [pwSteps, setPwSteps] = useState(1);     // 1 password (default) or 2 if GATE_PASSWORD_2 is set on the server
  const [useTg, setUseTg] = useState(false);       // Telegram approval switched on on the server?
  const [pendingId, setPendingId] = useState("");  // waiting for the owner to tap Allow on Telegram
  const [left, setLeft] = useState(0);

  const loadCaptcha = useCallback(async () => {
    setAnswer("");
    try {
      const r = await fetch(SERVER_URL + "/api/gate/captcha", { cache: "no-store" });
      setCap(await r.json());
    } catch { setErr("Can't reach the server."); }
  }, []);
  useEffect(() => { loadCaptcha(); }, [loadCaptcha]);
  useEffect(() => { fetch(SERVER_URL + "/api/gate/status", { headers: { "X-Device-Token": getDevice() } }).then(r => r.json()).then(d => { setUseTg(!!d.telegram && !d.trusted); setPwSteps(d.twoPw ? 2 : 1); }).catch(() => {}); }, []);

  const reset = (msg) => { setPendingId(""); setStep(1); setStepToken(""); setPw(""); setErr(msg || ""); loadCaptcha(); };

  // Step 3: poll until the owner taps Allow / Deny in Telegram
  useEffect(() => {
    if (!pendingId) return;
    const end = Date.now() + 180000;
    const tick = setInterval(() => setLeft(Math.max(0, Math.round((end - Date.now()) / 1000))), 500);
    const poll = setInterval(async () => {
      try {
        const d = await (await fetch(SERVER_URL + "/api/gate/poll?id=" + pendingId, { cache: "no-store" })).json();
        if (d.status === "approved") { if (d.deviceToken) setDevice(d.deviceToken); setGate(d.token || ""); onPass(); }
        else if (d.status === "denied") reset("Access was denied.");
        else if (d.status === "expired") reset("No answer in time — please start again.");
      } catch {}
    }, 2000);
    return () => { clearInterval(tick); clearInterval(poll); };
    // eslint-disable-next-line
  }, [pendingId]);
  // captcha expires after 2 minutes → quietly swap in a fresh one
  useEffect(() => { const t = setInterval(loadCaptcha, 100000); return () => clearInterval(t); }, [loadCaptcha]);

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true); setErr("");
    try {
      const res = await fetch(SERVER_URL + "/api/gate/verify", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ step, stepToken, password: pw, captchaId: cap.id, captcha: answer, deviceToken: getDevice() }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) {
        setErr(d.error || "Something went wrong.");
        if (d.restart) { setStep(1); setStepToken(""); }
        setPw(""); loadCaptcha();
      } else if (d.pendingId) {
        if (d.deviceInvalid) clearDevice();   // saved device was revoked/expired → full Telegram check this time
        setUseTg(true);
        setLeft(d.ttl || 180); setPendingId(d.pendingId);
      } else if (d.stepToken) {
        setStepToken(d.stepToken); setStep(2); setPw(""); loadCaptcha();
      } else {
        setGate(d.token || ""); onPass();
      }
    } catch { setErr("Can't reach the server."); loadCaptcha(); }
    setBusy(false);
  };

  const input = { width: "100%", boxSizing: "border-box", padding: "13px 14px", borderRadius: 10, border: "1px solid #2a2a2a", background: "#0e0e0e", color: "#fff", fontSize: 16, outline: "none" };
  return (
    <div style={{ minHeight: "100vh", background: "#000", display: "flex", alignItems: "center", justifyContent: "center", padding: 16, fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}>
      <style>{".gate-cap svg{width:100%;height:auto;display:block}"}</style>
      <form onSubmit={submit} autoComplete="off" style={{ width: "100%", maxWidth: 360, display: "flex", flexDirection: "column", gap: 14 }}>
        <div style={{ textAlign: "center", color: "#fff" }}>
          <div style={{ fontSize: 34 }}>🔒</div>
          <div style={{ fontSize: 20, fontWeight: 700, marginTop: 6 }}>Private access</div>
          <div style={{ fontSize: 13, color: "#777", marginTop: 4 }}>Verification {pendingId ? pwSteps + 1 : step} of {pwSteps + (useTg ? 1 : 0)}</div>
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          {Array.from({ length: pwSteps + (useTg ? 1 : 0) }, (_, i) => i + 1).map(n => <div key={n} style={{ flex: 1, height: 3, borderRadius: 2, background: n <= (pendingId ? pwSteps + 1 : step) ? "#fff" : "#2a2a2a" }} />)}
        </div>
        {pendingId ? (<>
          <div style={{ textAlign: "center", color: "#fff", padding: "26px 0" }}>
            <div style={{ fontSize: 40 }}>📲</div>
            <div style={{ fontWeight: 700, marginTop: 10 }}>Waiting for approval</div>
            <div style={{ color: "#777", fontSize: 13, marginTop: 6 }}>A message was sent to the owner on Telegram.<br />This page opens as soon as “Yes, it’s me” is tapped.</div>
            <div style={{ color: "#555", fontSize: 13, marginTop: 14 }}>{Math.floor(left / 60)}:{String(left % 60).padStart(2, "0")}</div>
          </div>
          <button type="button" onClick={() => reset("")} style={{ padding: 12, borderRadius: 10, border: "1px solid #2a2a2a", background: "transparent", color: "#aaa", cursor: "pointer" }}>Cancel</button>
        </>) : (<>
        <input key={step} type="password" autoFocus value={pw} onChange={e => setPw(e.target.value)} placeholder={step === 1 ? "Password" : "Second password"} style={input} autoComplete="new-password" />
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <div className="gate-cap" style={{ flex: 1, borderRadius: 10, overflow: "hidden", border: "1px solid #2a2a2a", lineHeight: 0, minHeight: 64 }} dangerouslySetInnerHTML={{ __html: cap.svg }} />
          <button type="button" onClick={loadCaptcha} title="New captcha" aria-label="New captcha" style={{ width: 46, height: 46, borderRadius: 10, border: "1px solid #2a2a2a", background: "#0e0e0e", color: "#fff", fontSize: 20, cursor: "pointer" }}>↻</button>
        </div>
        <input value={answer} onChange={e => setAnswer(e.target.value)} placeholder="Type the characters above" style={{ ...input, letterSpacing: 2, textTransform: "uppercase" }} autoCapitalize="characters" autoComplete="off" spellCheck={false} />
        {err && <div style={{ color: "#f87171", fontSize: 13, textAlign: "center" }}>{err}</div>}
        <button type="submit" disabled={busy || !pw || !answer} style={{ padding: 14, borderRadius: 10, border: "none", background: "#fff", color: "#000", fontWeight: 700, fontSize: 15, cursor: "pointer", opacity: busy || !pw || !answer ? 0.5 : 1 }}>
          {busy ? "Checking…" : step < pwSteps ? "Continue" : useTg ? "Send to Telegram" : "Unlock"}
        </button>
        </>)}
      </form>
    </div>
  );
}